import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, query, where } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { X, ClipboardList } from "lucide-react";
import FormRenderer from "../FormRenderer";
import { useAuth } from "../../hooks/AuthenticationProvider";

const FormListModal = ({
  organizationId,
  residentId,
  residentFullName,
  onClose,
}) => {
  const { user } = useAuth(); // Get logged-in user from authentication

  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const formsCollectionRef = collection(
          db,
          `organizations/${organizationId}/forms`
        );
        const querySnapshot = await getDocs(formsCollectionRef);
        const formsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setForms(formsData);
      } catch (error) {
        console.error("Error fetching forms:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, [organizationId]);

  const handleFormSubmit = async (formData) => {
    try {
      // Ensure residentFullName is valid before submission
      if (!residentFullName) {
        console.error("Error: residentFullName is undefined");
        alert("Error submitting form. Resident's full name is missing.");
        return;
      }

      // Create a complete representation of form elements
      // This ensures header images and other non-interactive elements are preserved
      const elementMetadata = {};
      const completeResponses = { ...formData };

      // Process all form elements, not just the ones with user interaction
      selectedForm.elements.forEach((element) => {
        const uniqueId = element.uniqueId;

        // Preserve element metadata
        elementMetadata[uniqueId] = {
          label: element.label,
          type: element.type,
          options: element.options,
          valueType: element.valueType,
        };

        // For elements like headerImage, preserve their data even if not in formData
        if (element.type === "headerImage" && !completeResponses[uniqueId]) {
          completeResponses[uniqueId] = {
            imageUrl: element.imageUrl,
            fileName: element.fileName,
            size: element.size || "medium",
            alignment: element.alignment || "center",
            // Add default value to fix the undefined error
            customHeight: element.customHeight || null,
          };
        }

        // Handle textBox which doesn't require user input
        if (element.type === "textBox" && !completeResponses[uniqueId]) {
          completeResponses[uniqueId] = element.content || "";
        }
      });

      const submissionData = {
        formId: selectedForm.id,
        formName: selectedForm.name,
        residentId,
        residentFullName,
        submittedBy: {
          userId: user?.uid || "Unknown",
          email: user?.email || "Unknown",
          displayName: user?.displayName || "Unknown",
        },
        dateSubmitted: new Date(),
        responses: completeResponses,
        elementMetadata: elementMetadata,
        elementOrder:
          selectedForm.elementOrder ||
          selectedForm.elements.map((e) => e.uniqueId),
      };

      // SAVE TO RESIDENT'S formSubmissions SUBCOLLECTION ONLY
      await addDoc(
        collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/formSubmissions`
        ),
        submissionData
      );

      // Only for applications, also save to applications collection
      if (selectedForm.isApplication) {
        await addDoc(
          collection(db, `organizations/${organizationId}/applications`),
          submissionData
        );
      }

      alert("Form submitted successfully!");
      setSelectedForm(null);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Error submitting form. Please try again.");
    }
  };

  if (selectedForm) {
    return (
      <FormRenderer
        form={selectedForm}
        onSubmit={handleFormSubmit}
        onClose={() => setSelectedForm(null)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
      <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <ClipboardList className="text-blue-400" size={20} />
            </div>
            <h3 className="text-xl font-semibold text-white">
              Available Forms
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="text-slate-400 hover:text-slate-300" size={20} />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="text-center text-slate-400">Loading forms...</div>
          ) : forms.length === 0 ? (
            <div className="text-center text-slate-400">No forms available</div>
          ) : (
            <div className="space-y-4">
              {forms.map((form) => (
                <button
                  key={form.id}
                  onClick={() => setSelectedForm(form)}
                  className="w-full flex items-center justify-between p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg text-white transition-colors"
                >
                  <span>{form.name}</span>
                  <span className="text-sm text-slate-400">
                    {new Date(
                      form.dateCreated.seconds * 1000
                    ).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormListModal;
